import { Input, Popover, Typography } from 'antd'
import { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import useErrorModal from '../../../../../hooks/api/use-error-modal'
import { useUpdatePrintJobMutation } from '../../../../../store/api/print-jobs-api'
import PrintJob from '../../../../../types/api/print-job'

interface PrintJobNoteCellProps {
    printJob: PrintJob
    editable: boolean
}

const NotePreview = styled.span<{ $muted?: boolean }>`
    display: block;
    box-sizing: border-box;
    width: 124px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    color: ${({ $muted, theme }) => ($muted ? theme.colorTextPlaceholder : 'inherit')};
`

const NoteTextArea = styled(Input.TextArea)`
    width: 280px;
`

const PrintJobNoteCell: FC<PrintJobNoteCellProps> = ({ printJob, editable }) => {
    const [value, setValue] = useState(printJob.printerComment ?? '')
    const savedValueRef = useRef(printJob.printerComment ?? '')
    const valueRef = useRef(value)
    const openRef = useRef(false)
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const [updatePrintJob, updatePrintJobState] = useUpdatePrintJobMutation()

    valueRef.current = value

    useErrorModal(updatePrintJobState, 'Не удалось сохранить примечание')

    useEffect(() => {
        const synced = printJob.printerComment ?? ''
        const dirty = valueRef.current.trim() !== savedValueRef.current.trim()

        if (openRef.current && dirty) return

        setValue(synced)
        savedValueRef.current = synced
    }, [printJob.printerComment])

    const save = () => {
        if (!editable) return

        const nextValue = valueRef.current.trim()

        if (nextValue === savedValueRef.current.trim()) {
            return
        }

        updatePrintJob({
            docId: printJob.documentId,
            body: { data: { printerComment: nextValue } },
        })
            .unwrap()
            .then(() => {
                savedValueRef.current = nextValue
            })
    }

    const handleOpenChange = (nextOpen: boolean) => {
        openRef.current = nextOpen

        if (!nextOpen) {
            save()
        }
    }

    const preview = value.trim()

    if (!editable && !preview) {
        return <Typography.Text>—</Typography.Text>
    }

    return (
        <Popover
            trigger={['hover', 'click']}
            mouseEnterDelay={0.2}
            mouseLeaveDelay={0.3}
            placement='left'
            onOpenChange={handleOpenChange}
            content={
                <NoteTextArea
                    ref={textAreaRef}
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    placeholder='Примечание'
                    value={value}
                    readOnly={!editable}
                    onChange={event => setValue(event.target.value)}
                    onClick={event => event.stopPropagation()}
                    onMouseDown={event => event.stopPropagation()}
                />
            }
        >
            <NotePreview
                $muted={!preview}
                onClick={event => {
                    event.stopPropagation()
                    window.setTimeout(() => textAreaRef.current?.focus(), 0)
                }}
                onMouseDown={event => event.stopPropagation()}
            >
                {preview || 'Примечание'}
            </NotePreview>
        </Popover>
    )
}

export default PrintJobNoteCell
